"use client";

import { useRouter } from "@bprogress/next";
import emojiData from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { useMutation } from "@tanstack/react-query";
import { ChevronLeft, Play, Smile, X } from "lucide-react";
import { useTheme } from "next-themes";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { FeelingsModal } from "@/components/dialog/feelings-modal";
import { ImageUpload } from "@/components/image-upload";
import { LocationPicker } from "@/components/location-picker";
import VisibilityDropdown from "@/components/posts/visibility-dropdown";
import { Tooltip } from "@/components/tooltip-content";
import LoadingUi from "@/components/ui-loading";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/image";
import { Textarea } from "@/components/ui/textarea";
import { MAX_FILES, MAX_TOTAL_SIZE_BYTES, MAX_TOTAL_SIZE_MB } from "@/constants";
import { createPost, deletePostMedia, editPost } from "@/lib/apis/posts";
import { showError, showSuccess } from "@/lib/toast";
import { useStore } from "@/store/store";

type Visibility = "public" | "friends" | "private" | "only me";

type CreatePostFormProps = {
  isEdit?: boolean;
  post?: PostsEntity;
  setShowEditDialog?: (open: boolean) => void;
};

export function CreatePostForm({ isEdit, post, setShowEditDialog }: CreatePostFormProps) {
  const { user, setIsOpen, setUser, feeling, setFeeling, files, setFiles, removeFile, handleFeelingSelect } = useStore();
  const { theme } = useTheme();

  const [content, setContent] = useState(post?.content || "");
  const [selectedLocation, setSelectedLocation] = useState<{ name: string; city: string } | null>(
    post?.location
      ? (() => {
        const [name, city] = post.location.split(",");
        return { name: name?.trim(), city: city?.trim() };
      })()
      : null,
  );
  const [visibility, setVisibility] = useState<Visibility>((post?.privacy as Visibility) || "public");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [background, setBackground] = useState(post?.background || "");
  const [showBackground, setShowBackground] = useState(false);
  const [showFeelingsModalPost, setShowFeelingsModalPost] = useState(false);
  const [existingMedia, setExistingMedia] = useState(post?.media || []);
  const router = useRouter();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const { mutate: deleteMediaMutate, isPending: deleteLoading } = useMutation({
    mutationFn: deletePostMedia,
    onError: (error) => {
      showError(error.message);
    },
  });

  const hasContent = useMemo(() => content.trim().length > 0 || files.length > 0, [content, files]);
  const backgroundStyle = useMemo(() => {
    return background.startsWith("bg-gradient") ? "text-white placeholder:text-white/70" : "";
  }, [background]);

  const handleResetForm = useCallback(() => {
    setContent("");
    setFiles([]);
    setSelectedLocation(null);
    setBackground("");
    setFeeling(null);
  }, []);

  const incrementUserPostCount = useCallback(() => {
    if (user) {
      setUser({ ...user, totalPosts: Number(user?.totalPosts ?? 0) + 1 });
    }
  }, [user, setUser]);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: FormData) => {
      if (isEdit && post?.id) {
        return editPost(post.id, payload);
      }
      return createPost(payload);
    },
    onSuccess: (data) => {
      if (isEdit) {
        showSuccess("Post updated request is submitted. You'll be notified when it's live");
        setShowEditDialog?.(false);
      }
      else {
          if (data.data?.status === "published") {
          toast.success("Your post is now live", {
            position: "top-right",
            action: {
              label: "View",
              onClick: () => router.push(`/posts/${data.data?.postId}`, { showProgress: true }),
            },
          });
        }
        else {
          showSuccess("Post submitted. You'll be notified when it's live");
        }
        incrementUserPostCount();
      }
      handleResetForm();
      setIsOpen(false);
    },
    onError: (error) => {
      showError(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasContent)
      return;

    const formData = new FormData();
    files.forEach(file => formData.append("media", file));
    formData.append("background", background);
    formData.append("content", content);
    if (feeling) {
      formData.append("feeling", `is ${feeling.emoji} feeling ${feeling.name}`);
    }
    if (selectedLocation) {
      formData.append("location", `${selectedLocation.name}, ${selectedLocation.city}`);
    }
    formData.append("privacy", visibility);
    mutate(formData);
  };

  const handleLocationSelect = useCallback((location: { name: string; city: string }) => {
    setSelectedLocation(location);
  }, []);

  const handleLocationRemove = useCallback(() => {
    setSelectedLocation(null);
  }, []);

  const handleDeleteMedia = useCallback((mediaId: string) => {
    if (!post)
      return;
    setExistingMedia(prev => prev.filter((m: MediaContent) => m.id !== mediaId));

    deleteMediaMutate({
      mediaId,
      postId: post?.id,
    });
  }, []);

  const handleFileSelect = useCallback((newFiles: File[]) => {
    if (background) {
      showError("Cannot add files with background selected");
      return;
    }

    const totalFiles = existingMedia.length + files.length + newFiles.length;

    if (totalFiles > MAX_FILES) {
      showError(`You can upload maximum ${MAX_FILES} files`);
      return;
    }

    const newTotalSize = [...files, ...newFiles].reduce(
      (total, file) => total + file.size,
      0,
    );

    if (newTotalSize > MAX_TOTAL_SIZE_BYTES) {
      showError(`Total size must be less than ${MAX_TOTAL_SIZE_MB}MB`);
      return;
    }

    if (!background) {
      setFiles(newFiles);
    }
  }, [background, files, existingMedia]);

  const handleFileRemove = useCallback((index: number) => {
    removeFile(index);
  }, []);

  const handleEmojiSelect = useCallback((emoji: { native: string }) => {
    setContent(prev => prev + emoji.native);
    setShowEmojiPicker(false);
  }, []);

  const handleFeelingRemove = useCallback(() => {
    setFeeling(null);
  }, []);

  const onSelect = useCallback((feeling: Feeling) => {
     handleFeelingSelect(feeling);
     setShowFeelingsModalPost(false);
  }, []);

  const handleBackgroundSelect = useCallback((bgClass: string) => {
    setBackground(bgClass);
    if (files.length > 0) {
      setFiles([]);
    }
  }, [files]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current
        && !emojiPickerRef.current.contains(event.target as Node)
        && !(event.target as Element).closest("button[aria-label='emoji-picker']")
      ) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    if (isEdit && post?.feeling) {
      const parts = post.feeling.split(" ");
      if (parts.length >= 4 && parts[0] === "is" && parts[2] === "feeling") {
        const emoji = parts[1];
        const name = parts.slice(3).join(" ");
        setFeeling({ emoji, name });
      }
    }
  }, [post, isEdit]);

  const backgroundOptions = useMemo(() => [
    "bg-[#c500ff]",
    "bg-[#e2003b]",
    "bg-gradient-to-r from-teal-400 via-cyan-500 to-blue-500",
    "bg-gradient-to-r from-yellow-300 via-orange-400 to-red-500",
    "bg-gradient-to-br from-[#0f2027] via-[#203a43] to-[#2c5364]",
    "bg-gradient-to-br from-[#232526] via-[#414345] to-[#232526]",
    "bg-gradient-to-br from-[#141E30] to-[#243B55]",
    "bg-gradient-to-br from-[#000000] via-[#434343] to-[#000000]",
    "bg-gradient-to-br from-[#1f4037] to-[#99f2c8]",
    "bg-gradient-to-br from-[#373B44] to-[#4286f4]",
    "bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e]",
    "bg-gradient-to-br from-[#3a1c71] via-[#d76d77] to-[#ffaf7b]",
  ], []);

  const renderMediaPreviews = useMemo(() => {
    if (files.length === 0 && existingMedia.length === 0)
      return null;

    return (
      <div className="grid grid-cols-2 gap-2">

        {existingMedia.map(m => (
          <div key={m.id} className="relative">
            <div className="rounded-lg overflow-hidden border h-40">
              {m.type.startsWith("image")
                ? (
                  <OptimizedImage src={m.url} alt="old" className="w-full h-full object-cover" width={200} height={200} />
                )
                : (
                  <div className="relative h-full">
                    <OptimizedImage src={`https://res.cloudinary.com/${process.env.NEXT_PUBLIC_CLOUD_NAME}/video/upload/w_350,h_600,c_thumb,q_auto,f_jpg/${m.id}.jpg`} alt="old" className="w-full h-full object-cover" width={200} height={200} />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <Play className="h-12 w-12 text-white/80" />
                    </div>
                  </div>
                )}
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute top-2 right-2 h-8 w-8 bg-background/80 backdrop-blur-sm"
                onClick={() => handleDeleteMedia(m.id)}
                disabled={deleteLoading}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        {files.map((file, index) => (
          <div key={`${file.name}-${index}`} className="relative">
            <div className="rounded-lg overflow-hidden border border-border h-40">
              {file.type.startsWith("image/")
                ? (
                  <OptimizedImage
                    src={URL.createObjectURL(file)}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                    width={200}
                    height={200}
                  />
                )
                : (
                  <div className="relative h-full">
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <Play className="h-12 w-12 text-white/80" />
                    </div>
                    <video
                      src={URL.createObjectURL(file)}
                      className="w-full h-full object-cover pointer-events-none"
                    />
                  </div>
                )}
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute top-2 right-2 h-8 w-8 bg-background/80 backdrop-blur-sm"
                onClick={() => handleFileRemove(index)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    );
  }, [files, existingMedia, handleFileRemove]);

  const renderBackgroundOptions = useMemo(() => {
    if (files.length > 0 || existingMedia.length > 0)
return null;

    return (
      <div className="flex justify-between items-center">
        <div className="flex-1 flex items-center gap-2">
          {showBackground
            ? (
              <button
                onClick={() => {
                  setShowBackground(false);
                  handleBackgroundSelect("");
                }}
                className="cursor-pointer w-[32px] h-[32px] rounded-md bg-black flex justify-center items-center"
                type="button"
              >
                <ChevronLeft />
              </button>
            )
            : (
              <button
                onClick={() => setShowBackground(true)}
                className="cursor-pointer"
                type="button"
              >
                <OptimizedImage
                  alt="Color Icon"
                  src="/colors.png"
                  width={35}
                  height={35}
                  className="w-[35px] h-[35px] object-cover"
                />
              </button>
            )}
          {showBackground && (
            <div className="flex max-w-[220px] md:max-w-[460px] gap-2 overflow-x-auto flex-nowrap scrollbar-hide">
              {backgroundOptions.map(bgClass => (
                <button
                  key={bgClass}
                  type="button"
                  onClick={() => handleBackgroundSelect(bgClass)}
                  className={`w-[32px] h-[32px] rounded-md cursor-pointer flex-shrink-0 ${bgClass}`}
                  aria-label={`Select ${bgClass} background`}
                />
              ))}
            </div>
          )}
        </div>
        <Tooltip title="Feeling">
          <button
            onClick={() => setShowFeelingsModalPost(true)}
            className="cursor-pointer"
            type="button"
          >
            <Smile className="md:h-5 md:w-5 h-4 w-4" />
          </button>
        </Tooltip>
      </div>
    );
  }, [
    files,
    showBackground,
    backgroundOptions,
    handleBackgroundSelect,
    feeling,
    handleFeelingRemove,
  ]);

  const renderLocationBadge = useMemo(() => {
    if (!selectedLocation)
      return null;

    return (
      <div className="flex items-center">
        <Badge variant="outline" className="flex gap-1 ml-12">
          <span>
            📍
            {" "}
            {selectedLocation.name}
            ,
            {" "}
            {selectedLocation.city}
          </span>
          <button type="button" onClick={handleLocationRemove} className="ml-1">
            <X className="h-3 w-3" />
          </button>
        </Badge>
      </div>
    );
  }, [selectedLocation, handleLocationRemove]);

  return (
    <>
    <form onSubmit={handleSubmit} className="flex flex-col h-full bg-card">
      <div className="sticky top-0 p-4 z-10  border-b border-border">
        <h1 className="text-xl font-semibold text-center">
          {isEdit ? "Edit" : "Create"}
          {" "}
          Post
        </h1>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isPending && <LoadingUi title="Posting" className="absolute inset-0 bg-background/80 z-20" />}

        <div className="flex gap-3">
          <Avatar>
            {user?.avatar
              ? (
                <AvatarImage src={user.avatar} alt={user.fullName} />
              )
              : (
                <AvatarFallback>{user?.fullName?.charAt(0)}</AvatarFallback>
              )}
          </Avatar>
          <div>
            <p>
              {user?.fullName}
              {" "}
              {feeling && (
                <>
                  is
                  {" "}
                  {feeling.emoji}
                  {" "}
                  feeling
                  {" "}
                  <strong>{feeling.name}</strong>
                </>
              )}

            </p>
            <span className="text-gray-400">
              @
              {user?.username}
            </span>
          </div>
        </div>

        <div className={`rounded-lg ${background} transition-colors duration-200`}>
          <Textarea
            ref={textareaRef}
            placeholder="What's on your mind?"
            value={content}
            onChange={e => setContent(e.target.value)}
            className={`flex-1 resize-none border-none focus-visible:ring-0 focus-visible:ring-offset-0 min-h-[100px] ${backgroundStyle}`}
            style={{
              backgroundColor: "transparent",
              color: background.startsWith("bg-gradient") ? "white" : "inherit",
            }}
          />
        </div>

        {renderBackgroundOptions}
        {renderLocationBadge}
        {renderMediaPreviews}
      </div>

      <div className="sticky bottom-0 z-10 border-t border-border p-4">
        <div className="flex justify-between items-center">
          <div className="flex relative gap-2">
            {showEmojiPicker && (
              <div ref={emojiPickerRef} className="absolute -bottom-20 overflow-hidden mb-3 left-2 z-50">
                <Picker
                  data={emojiData}
                  onEmojiSelect={handleEmojiSelect}
                  theme={theme}
                  previewPosition="none"
                  searchPosition="none"
                />
              </div>
            )}
            <ImageUpload
              onImageSelect={handleFileSelect}
              accept="image/*, video/*"
              disabled={!!background}
              multiple
            />
            <Tooltip title="Emoji">
              <Button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0"
                aria-label="emoji-picker"
              >
              <Smile className="md:h-5 md:w-5 h-4 w-4" />
              </Button>
            </Tooltip>
            {
            (files.length > 0 || existingMedia.length > 0) && (
              <Tooltip title="Feeling/activity">
                            <Button
                              onClick={() => setShowFeelingsModalPost(true)}
                              className="cursor-pointer text-primary shrink-0"
                              type="button"
                              variant="ghost"
                              size="icon"
                            >
                              <Smile className="md:h-5 md:w-5 h-4 w-4" />
                            </Button>
              </Tooltip>
              )
            }
            <LocationPicker onLocationSelect={handleLocationSelect} />
          </div>
          <div className="flex items-center gap-2">
            <VisibilityDropdown
              visibility={visibility}
              setVisibility={setVisibility}
            />
            <Button
              type="submit"
              disabled={!hasContent}
              className="rounded-full"
            >
              {isEdit ? "Update" : "Post"}
            </Button>
          </div>
        </div>
      </div>
    </form>

    <FeelingsModal
      open={showFeelingsModalPost}
      onOpenChange={setShowFeelingsModalPost}
      onSelect={onSelect}
    />
    </>
  );
}
